import zodToJsonSchema from 'zod-to-json-schema';
import { tool, jsonSchema } from 'ai';
import { z } from 'zod';
import { emit } from '@zapier/events-toolkit-browser';

function makeServerTool(tool) {
  return tool;
}
function makeClientTool(tool) {
  return tool;
}
function makeFinalTool(tool) {
  return tool;
}
function makeToolComponent(component) {
  return component;
}

function extractComponents(tools) {
  return Object.fromEntries(
    tools.filter(
      (tool) => tool.component !== void 0
    ).map((tool) => [tool.name, tool.component])
  );
}
function serializeTools(tools) {
  return tools.map(({ name, description, parameters }) => ({
    name,
    description,
    parameters: zodToJsonSchema(parameters)
  }));
}
function createOnToolCall(tools) {
  const execMap = Object.fromEntries(tools.map((t) => [t.name, t.execute]));
  return async (params) => {
    if (!params) {
      console.warn(
        "createOnToolCall: params is undefined.\n\nDid you setState(createOnToolCall(...))? If so, use setState(() => createOnToolCall(...)) instead."
      );
      return Promise.resolve(void 0);
    }
    const { toolCall } = params;
    const { toolName, args } = toolCall;
    const handler = execMap[toolName];
    if (!handler) return Promise.resolve(void 0);
    return handler(args);
  };
}

async function createServerTools({
  serverTools = [],
  clientTools = [],
  disableErrorWrapping = false,
  parentSpanId,
  logger
}) {
  const tools = {};
  serverTools.forEach((serverTool) => {
    const hasExecuteFn = "execute" in serverTool;
    if (!hasExecuteFn) {
      const resolvedParameters = serverTool.parametersToSchema ? serverTool.parametersToSchema(serverTool.parameters) : serverTool.parameters;
      tools[serverTool.name] = tool({
        description: serverTool.description,
        parameters: resolvedParameters
      });
      return;
    }
    const augmentedTool = tool({
      description: serverTool.description,
      parameters: resolveServerToolParameters({
        serverTool,
        disableErrorWrapping
      }),
      execute: (() => {
        let wrappedExecute = serverTool.execute;
        if (!disableErrorWrapping) {
          wrappedExecute = wrapExecuteWithValidation(
            wrappedExecute,
            serverTool.parameters
          );
        }
        if (logger) {
          wrappedExecute = wrapExecuteWithTracing(
            wrappedExecute,
            serverTool.name,
            parentSpanId,
            logger
          );
        }
        return wrappedExecute;
      })()
    });
    tools[serverTool.name] = augmentedTool;
  });
  clientTools.forEach((clientTool) => {
    tools[clientTool.name] = tool({
      description: clientTool.description,
      parameters: jsonSchema(clientTool.parameters)
    });
  });
  return tools;
}
function resolveServerToolParameters({
  serverTool,
  disableErrorWrapping
}) {
  if (disableErrorWrapping) {
    if (serverTool.parametersToSchema) {
      return serverTool.parametersToSchema(serverTool.parameters);
    }
    return serverTool.parameters;
  }
  if (serverTool.parametersToSchema) {
    return createValidationBypassForAISchema(
      serverTool.parametersToSchema(serverTool.parameters)
    );
  }
  return createValidationBypassForZodSchema(serverTool.parameters);
}
function createErrorObject(error) {
  const isDevelopment = process.env.NODE_ENV === "development";
  if (error instanceof z.ZodError) {
    return {
      $error: {
        message: "Validation failed",
        issues: error.errors.map((issue) => ({
          path: issue.path.join("."),
          message: issue.message
        })),
        ...isDevelopment && { stack: error.stack }
      }
    };
  }
  if (error instanceof Error) {
    return {
      $error: {
        message: error.message,
        ...isDevelopment && { stack: error.stack }
      }
    };
  }
  return {
    $error: {
      message: typeof error === "string" ? error : "Unknown error occurred"
    }
  };
}
function createValidationBypassForZodSchema(schema) {
  const bypass = Object.create(schema);
  bypass.parse = (data) => data;
  bypass.safeParse = (data) => ({ success: true, data });
  bypass.parseAsync = async (data) => data;
  bypass.safeParseAsync = async (data) => ({ success: true, data });
  return bypass;
}
function createValidationBypassForAISchema(schema) {
  const bypass = Object.create(schema);
  bypass.validate = (value) => {
    return {
      success: true,
      value
    };
  };
  return bypass;
}
function wrapExecuteWithTracing(execute, toolName, parentSpanId, logger) {
  if (!logger) {
    return execute;
  }
  return async (args, options) => {
    return await logger.traced(
      async (span) => {
        span.log({ input: args });
        if (options?.toolCallId) {
          span.log({ metadata: { toolCallId: options.toolCallId } });
        }
        try {
          const result = await execute(args, options);
          span.log({ output: result });
          return result;
        } catch (error) {
          span.log({
            error: error instanceof Error ? error.message : String(error)
          });
          throw error;
        }
      },
      {
        type: "tool",
        name: `Server Tool: ${toolName}`,
        ...parentSpanId && { parent: parentSpanId }
      }
    );
  };
}
function wrapExecuteWithValidation(execute, schema) {
  return async (args, options) => {
    try {
      const validatedArgs = schema.parse(args);
      return await execute(validatedArgs, options);
    } catch (error) {
      return createErrorObject(error);
    }
  };
}

function generateHandoffContext(copilots) {
  const copilotList = copilots.map((copilot) => {
    const paramInfo = copilot.parameters ? ` (requires parameters)` : "";
    return `- ${copilot.id}: ${copilot.description}${paramInfo}`;
  }).join("\n");
  return `Available copilots for handoff:
${copilotList}`;
}
function createCopilotRegistry(copilots) {
  const registry = {
    copilots,
    getById(id) {
      return copilots.find((c) => c.id === id);
    },
    createIdSchema() {
      const ids = copilots.map((c) => c.id);
      if (ids.length === 0) {
        throw new Error("Cannot create schema from empty registry");
      }
      const enumValues = ids;
      const description = `The ID of the copilot:

${copilots.map((c) => `* ${c.id}: ${c.name} (${c.description})`).join("\n")}`;
      return z.enum(enumValues).describe(description);
    },
    generateSystemPrompt(options) {
      const { basePrompt } = options;
      const handoffContext = generateHandoffContext(copilots);
      return basePrompt.includes("{{handoffPrompt}}") ? basePrompt.replace("{{handoffPrompt}}", handoffContext) : `${basePrompt}

${handoffContext}`;
    }
  };
  return registry;
}

function getZapierDomain() {
  const isStaging = process.env.VERCEL_ENV === "preview";
  return isStaging ? "zapier-staging.com" : "zapier.com";
}
function getZapierBaseUrl() {
  return process.env.NEXT_PUBLIC_ZAPIER_ORIGIN || `https://${getZapierDomain()}`;
}
function getAgentsBaseUrl() {
  if (process.env.NEXT_PUBLIC_AGENTS_URL) {
    return process.env.NEXT_PUBLIC_AGENTS_URL;
  }
  return `https://agents.${getZapierDomain()}`;
}
function getCanvasBaseUrl() {
  if (process.env.NEXT_PUBLIC_CANVAS_URL) {
    return process.env.NEXT_PUBLIC_CANVAS_URL;
  }
  return getZapierBaseUrl();
}
function getEditorBaseUrl() {
  if (process.env.NEXT_PUBLIC_EDITOR_URL) {
    return process.env.NEXT_PUBLIC_EDITOR_URL;
  }
  return getZapierBaseUrl();
}
function getInterfacesBaseUrl() {
  if (process.env.NEXT_PUBLIC_INTERFACES_URL) {
    return process.env.NEXT_PUBLIC_INTERFACES_URL;
  }
  return `https://interfaces.${getZapierDomain()}`;
}
function getTablesBaseUrl() {
  if (process.env.NEXT_PUBLIC_TABLES_URL) {
    return process.env.NEXT_PUBLIC_TABLES_URL;
  }
  return `https://tables.${getZapierDomain()}`;
}
function getZapierOrigin() {
  return process.env.NEXT_PUBLIC_ZAPIER_ORIGIN || process.env.ZAPIER_ORIGIN || `https://${getZapierDomain()}`;
}
function getSemanticSearchOrigin() {
  return process.env.SEMANTIC_SEARCH_ORIGIN || "https://semanticsearch.zapier.com";
}
function getCustomActionsOrigin() {
  return process.env.CUSTOM_ACTIONS_ORIGIN || `${getZapierBaseUrl()}/app/extensions`;
}
function getUserContextAPIOrigin() {
  return process.env.USER_CONTEXT_API_ORIGIN || `https://usercontextapi.${getZapierDomain()}`;
}
function decodeSpanIdProjectName$1(spanId) {
  try {
    const decoded = atob(spanId);
    const jsonStart = decoded.indexOf('{"');
    if (jsonStart !== -1) {
      const jsonPart = decoded.slice(jsonStart);
      const parsed = JSON.parse(jsonPart);
      return parsed.compute_object_metadata_args?.project_name || null;
    }
  } catch (error) {
    console.error("Error decoding span ID:", error);
  }
  return null;
}
function getBraintrustLogsUrl(threadId, spanId, braintrustProjectName) {
  const organization = getZapierDomain();
  const projectName = spanId ? decodeSpanIdProjectName$1(spanId) || braintrustProjectName || "ai-toolkit-copilot" : braintrustProjectName || "ai-toolkit-copilot";
  const searchParams = encodeURIComponent(
    JSON.stringify({
      filter: [
        {
          label: `thread_id = "${threadId}"`,
          text: `thread_id = "${threadId}"`,
          originType: "btql"
        }
      ]
    })
  );
  return `https://www.braintrust.dev/app/${organization}/p/${projectName}/logs?search=${searchParams}`;
}

const globalCopilots = [
  {
    id: "agents",
    name: "Agent Builder",
    description: "Create and edit Zapier Agents - AI-powered agents that can perform actions independently across your connected apps and services.",
    parameters: z.object({
      botId: z.string().nullable().describe("The ID of the Agent to edit, null if new Agent")
    }),
    generateUrl({ botId }) {
      const baseUrl = getAgentsBaseUrl();
      if (botId) {
        return `${baseUrl}/bots/${botId}/settings?tab=chat`;
      } else {
        return `${baseUrl}/bots?new=true`;
      }
    }
  },
  {
    id: "canvas",
    name: "Canvas Workflow Builder",
    description: "Visual workflow builder for creating and editing Zapier automations",
    parameters: z.object({
      canvasId: z.string().nullable().describe("The ID of the Canvas to edit, null if new Canvas")
    }),
    generateUrl: ({ canvasId }) => {
      const baseUrl = getCanvasBaseUrl();
      const canvasSuffix = canvasId ? `/app/canvas/${canvasId}` : "/app/canvas/ai";
      return `${baseUrl}${canvasSuffix}`;
    }
  },
  {
    id: "editor",
    name: "Zap Editor",
    description: "Create and edit Zaps in the Zap Editor -  an intuitive, no-code platform for building workflow automations with Zapier.",
    parameters: z.object({
      zapId: z.string().nullable().describe(
        "The ID of the Zap, null if creating new Zap. Fill in the ID of the Zap you want to edit, if known"
      ),
      stepId: z.string().nullable().describe(
        "The ID of the step to route to (null if Zap ID is not provided)"
      )
    }),
    generateUrl: ({ zapId, stepId }) => {
      const baseUrl = getEditorBaseUrl();
      if (zapId) {
        if (stepId) {
          return `${baseUrl}/editor/${zapId}/draft/${stepId}`;
        }
        return `${baseUrl}/editor/${zapId}/draft`;
      }
      return `${baseUrl}/webintent/create-zap`;
    }
  },
  {
    id: "interfaces",
    name: "Interface Builder",
    /**
     * Created by prompting Copilot in Interfaces the following:
     * "Imagine you are tasked with creating a description for Interfaces for other LLMs to consume. What description you would come up with? Keep it brief, focus on functionality. Use the search help docs rather than the tools you have available."
     */
    description: `
Zapier Interfaces is a no-code platform for building custom web pages and interactive applications.
It enables users to create multi-page websites and apps using drag-and-drop components including forms, tables, kanban boards, media blocks, buttons, dividers, and embedded content.
Interfaces integrates seamlessly with Zapier Tables for data storage and Zaps for workflow automation.

Key capabilities include:
- Interactive Components: Forms for data collection, tables for data display, kanban boards for project management, media blocks, buttons, and embedded content
- Multi-page Architecture: Create websites with multiple interconnected pages and customizable navigation
- Data Integration: Connect to Zapier Tables for database functionality and trigger Zaps for automated workflows
- Access Control: Configure public access, password protection, or user-specific permissions with various authentication methods
- Customization: Apply custom branding, themes, logos, and domain names
- Automation: Trigger workflows when users interact with components like form submissions or button clicks

Interfaces serves use cases like lead capture forms, client portals, employee onboarding sites, and custom business applications, providing a bridge between data collection, presentation, and automated processing.
`,
    parameters: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("new-project"),
        /**
         * We could validate the name here as per the schema in Interfaces, but we decided not to.
         * For us, it is much more important to keep the user "in the flow" rather than to interrupt the handoff.
         * We handle the validation on the Interfaces side and fallback to a default name if it fails.
         */
        name: z.string().nullable().describe(
          "The name of the new Interface. You MUST null this parameter unless the user explicitly mentions the name of the Interface they want to create."
        )
      }),
      z.object({
        type: z.literal("project"),
        projectId: z.string().cuid().describe(
          "The unique identifier (CUID) of the Zapier Interface to navigate to."
        )
      }),
      z.object({
        type: z.literal("page"),
        pageId: z.string().cuid().describe("The unique identifier (CUID) of the page to navigate to.")
      })
    ]),
    generateUrl(params) {
      const baseUrl = getInterfacesBaseUrl();
      switch (params.type) {
        case "new-project": {
          const url = new URL("/interfaces/new-from-copilot", baseUrl);
          if (params.name) {
            url.searchParams.set("name", params.name);
          }
          return url.toString();
        }
        case "project": {
          const url = new URL(
            `/interfaces/project/${params.projectId}`,
            baseUrl
          );
          url.searchParams.set("sideNavDrawer", "settings-copilot");
          return url.toString();
        }
        case "page": {
          const url = new URL(`/interfaces/page/${params.pageId}`, baseUrl);
          url.searchParams.set("sideNavDrawer", "settings-copilot");
          return url.toString();
        }
        default: {
          throw new Error("Invalid type");
        }
      }
    }
  },
  {
    id: "tables",
    name: "Table Builder",
    description: `Create and manage Zapier Tables - a flexible, no-code database that lets you store, organize, and
      automate data. Built to seamlessly integrate with your workflows across Zapier
      When a user provides a Table ID, always validate it first before attempting any handoff. State whether it's valid or invalid and why.
      Before using any Table ID in a handoff, validate that it:
      - Is exactly 26 characters. You can check this using the regex ^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$.
      - Contains only 0-9 and A-Z (uppercase)
      - Does NOT contain I, L, O, or U (these letters are excluded to avoid confusion with similar-looking numbers)
      
      IMPORTANT: You must manually check each character individually against the forbidden letters I, L, O, U before declaring a Table ID valid. Do not rely solely on regex matching - explicitly verify that none of these forbidden characters appear anywhere in the string.
      IMPORTANT: Do not relay your validation process to the user. Just state whether the Table ID is valid or invalid and why.

      Validation process:
      1. Check length is exactly 26 characters
      2. Manually scan each character and explicitly check: Is this character the LETTER I? Is this character the LETTER L? Is this character the LETTER O? Is this character the LETTER U? If ANY character matches these LETTERS I, L, O, or U, immediately declare the ID invalid.
      3. Verify all characters are 0-9 or A-Z (uppercase)
      4. When checking for forbidden characters, remember that the LETTER O and LETTER U are FORBIDDEN. Do not confuse the LETTER O with the NUMBER 0 (zero) - they are different characters. The NUMBER 0 is allowed, the LETTER O is forbidden.
      5. Only if all checks pass, declare the ID valid
      
      If validation fails, explaint to the user that the Table ID is invalid and concisely explain why.
      `,
    parameters: z.object({
      tableId: z.string().regex(
        /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/,
        "Invalid Table ID format"
      ).nullable().describe("The ID of the Table to edit, null if new Table")
    }),
    generateUrl({ tableId }) {
      const baseUrl = getTablesBaseUrl();
      if (tableId) {
        const url2 = new URL(`/app/tables/t/${tableId}`, baseUrl);
        url2.searchParams.set("sidebarOpen", "copilot");
        return url2.toString();
      }
      const url = new URL("/app/tables/new", baseUrl);
      return url.toString();
    }
  },
  {
    id: "memberhome",
    name: "Home Page",
    description: `
      Home is your personalized starting point for automation. Find relevant use cases, tools, and features tailored to your role and interests.
      What it does:
      - Shows use cases and tools based on your needs
      - Recommends next steps to get started or go deeper
      - Easily search and browse functionality
      When to use it:
      - ONLY when the user explicitly asks to go to the home page (e.g. "take me home", "show me the homepage", "go to my dashboard")
    `,
    generateUrl: () => {
      const baseUrl = getZapierBaseUrl();
      return `${baseUrl}/app/home`;
    }
  }
];
async function getGlobalCopilotRegistry() {
  return createCopilotRegistry(globalCopilots);
}

var HandoffStatus = /* @__PURE__ */ ((HandoffStatus2) => {
  HandoffStatus2["Initializing"] = "initializing";
  HandoffStatus2["None"] = "none";
  HandoffStatus2["Loading"] = "loading";
  HandoffStatus2["Ready"] = "ready";
  HandoffStatus2["Applied"] = "applied";
  HandoffStatus2["Error"] = "error";
  return HandoffStatus2;
})(HandoffStatus || {});
const HANDOFF_TOOL_NAME = "handoff";

const commonToolParams = z.object({
  displayMessage: z.string().describe(
    "A 8-12 word message to display to the user about why we are using this tool (e.g., 'Searching for relevant apps...', 'Let's take a look at your current workflows...')"
  )
});

const FEEDBACK_RATINGS = {
  EXPLICIT_BAD: -2,
  IMPLICIT_BAD: -1,
  IMPLICIT_GOOD: 1,
  EXPLICIT_GOOD: 2
};

const consoleServerLogger = {
  warn: (...args) => {
    console.warn(...args);
  },
  error: (...args) => {
    console.error(...args);
  },
  info: (...args) => {
    console.info(...args);
  },
  debug: (...args) => {
    console.debug(...args);
  },
  trace: (...args) => {
    console.trace(...args);
  }
};

const emitEventFromServer = async ({
  eventData,
  eventType,
  serverLogger = consoleServerLogger
}) => {
  if (process.env.NODE_ENV === "development") {
    return [false, void 0];
  }
  const url = `${process.env.NEXT_PUBLIC_ZAPIER_ORIGIN ?? "https://zapier.com"}/api/v4/tracking/event/`;
  try {
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        subject: eventType,
        properties: eventData
      })
    });
    return [true, void 0];
  } catch (e) {
    serverLogger.warn({ error: e, eventType }, "Error sending event");
    return [false, void 0];
  }
};
const emitEventFromClient = async (eventData, eventType) => {
  if (process.env.NODE_ENV === "development") {
    return [false, void 0];
  }
  return emit(eventData, eventType);
};
const trackCopilotToolExecutedAttemptEvent = ({
  eventData,
  serverLogger = consoleServerLogger
}) => {
  emitEventFromServer({
    eventData,
    eventType: "ai.tracking.CopilotToolExecutedAttemptEvent",
    serverLogger
  });
};
const trackCopilotUserFeedbackEvent = ({
  eventData,
  serverLogger = consoleServerLogger
}) => {
  emitEventFromServer({
    eventData,
    eventType: "ai.tracking.CopilotUserFeedbackEvent",
    serverLogger
  });
};
const trackCopilotPredictionOutputEvent = ({
  eventData,
  serverLogger = consoleServerLogger
}) => {
  emitEventFromServer({
    eventData,
    eventType: "ai.tracking.CopilotPredictionOutputEvent",
    serverLogger
  });
};
const trackCopilotInteractionEvent = (eventData) => {
  return emitEventFromClient(eventData, "ai.tracking.CopilotInteractionEvent");
};
const getCommonTrackingEventFields = ({
  copilotId,
  profile,
  conversationId,
  userMessageId,
  assetId
}) => {
  const isLoggedIn = profile.is_logged_in;
  const assetType = getAssetType(copilotId);
  return {
    event_id: crypto.randomUUID(),
    timestamp_ms: Date.now(),
    copilot_id: copilotId,
    copilot_thread_id: conversationId,
    correlation_id: conversationId,
    cross_product_conversation_id: conversationId,
    asset_type: assetType,
    ...assetType && assetId && { asset_id: assetId },
    user_message_id: userMessageId,
    user_request_id: userMessageId,
    customuser_id: isLoggedIn ? profile.id : void 0,
    account_id: isLoggedIn ? profile.current_account_id : void 0
  };
};
const FEEDBACK_RATINGS_MAP = {
  [FEEDBACK_RATINGS.EXPLICIT_BAD]: "EXPLICIT_BAD",
  [FEEDBACK_RATINGS.IMPLICIT_BAD]: "IMPLICIT_BAD",
  [FEEDBACK_RATINGS.IMPLICIT_GOOD]: "IMPLICIT_GOOD",
  [FEEDBACK_RATINGS.EXPLICIT_GOOD]: "EXPLICIT_GOOD"
};
const getUserRating = (rating) => {
  if (rating in FEEDBACK_RATINGS_MAP) {
    return FEEDBACK_RATINGS_MAP[rating];
  }
  return null;
};
const getAssetType = (copilotId) => {
  switch (copilotId) {
    case "canvas":
      return "canvas";
    case "chatbots":
      return "chatbot";
    case "editor":
      return "zap";
    case "interfaces":
      return "interface";
    case "tables":
      return "table";
    default:
      return void 0;
  }
};
const getCharacterQuantity = (assistantMessages, userMessage) => {
  return (userMessage?.content?.length ?? 0) + assistantMessages.reduce((sum, msg) => {
    let messageContentLength = 0;
    if (Array.isArray(msg.content)) {
      messageContentLength = msg.content.reduce((contentSum, contentItem) => {
        if (typeof contentItem === "object" && contentItem !== null) {
          if ("text" in contentItem && typeof contentItem.text === "string") {
            return contentSum + contentItem.text.length;
          }
          if ("toolName" in contentItem) {
            const messageLength = "args" in contentItem && contentItem.args && typeof contentItem.args === "object" ? "displayMessage" in contentItem.args ? contentItem.args.displayMessage.length : JSON.stringify(contentItem.args || {}).length : 0;
            return contentSum + messageLength;
          }
          return contentSum + JSON.stringify(contentItem).length;
        }
        return contentSum;
      }, 0);
    } else if (typeof msg.content === "object" && msg.content !== null && "text" in msg.content) {
      messageContentLength = msg.content.text?.length ?? 0;
    } else if (typeof msg.content === "string") {
      messageContentLength = msg.content.length;
    }
    return sum + messageContentLength;
  }, 0);
};

const trimStr = (str, length) => {
  if (typeof str !== "string") return str;
  if (str.length <= length) return str;
  const half = Math.floor(length / 2);
  return str.slice(0, half) + " [TRUNCATED]" + str.slice(-half);
};
const recurseTrim = (obj, length) => {
  if (typeof obj === "string") {
    return trimStr(obj, length);
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => recurseTrim(item, length));
  }
  if (obj && typeof obj === "object" && obj !== null) {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = recurseTrim(obj[key], length);
      }
    }
    return result;
  }
  return obj;
};

function isNewUserMessage(messages) {
  return !!(messages && messages.length > 0 && messages[messages.length - 1]?.role === "user");
}
function getTraceSpanId(headerSpanId, messages) {
  const isNewMessage = isNewUserMessage(messages);
  return isNewMessage ? void 0 : headerSpanId;
}
const SPAN_ID_HEADERS = {
  SPAN_ID: "x-braintrust-span-id"
};
function decodeSpanIdProjectName(spanId) {
  try {
    const decoded = atob(spanId);
    const jsonStart = decoded.indexOf('{"');
    if (jsonStart !== -1) {
      const jsonPart = decoded.slice(jsonStart);
      const parsed = JSON.parse(jsonPart);
      return parsed.compute_object_metadata_args?.project_name || null;
    }
  } catch (error) {
    console.error("Error decoding span ID:", error);
  }
  return null;
}

function extractSpanIdForTracing(request, messages) {
  const headerSpanId = request.headers.get(SPAN_ID_HEADERS.SPAN_ID) ?? void 0;
  const isNewUserMessage = messages && messages.length > 0 && messages[messages.length - 1]?.role === "user";
  return isNewUserMessage ? void 0 : headerSpanId;
}
function setSpanIdHeaders(response, spanId) {
  if (spanId) {
    response.headers.set(SPAN_ID_HEADERS.SPAN_ID, spanId);
  }
}
function createStreamResponseWithHeaders(stream, spanId) {
  const response = new Response(stream, {
    headers: {
      "Content-Type": "text/plain; charset=utf-8"
    }
  });
  setSpanIdHeaders(response, spanId);
  return response;
}

const camelSnakeToTitle = (str) => {
  if (!str) return "";
  let result = str.replace(/_/g, " ");
  result = result.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  result = result.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  return result.split(" ").filter((word) => word.length > 0).map((word) => {
    if (word.length === 1) {
      return word.toUpperCase();
    }
    if (word === word.toUpperCase() && word.length > 1) {
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(" ");
};
const getToolCallStatus = (toolInvocation, displayOverride) => {
  const isRecord = (v) => typeof v === "object" && v !== null;
  const getResultPayload = (invocation) => {
    const raw = invocation.result;
    if (!isRecord(raw)) return null;
    const nested = raw.result;
    return isRecord(nested) ? nested : raw;
  };
  const isCancelledByUser = (invocation) => {
    const payload2 = getResultPayload(invocation);
    return payload2?.cancelled === true;
  };
  if (toolInvocation.state !== "result") {
    return "loading";
  }
  if (displayOverride.isError) {
    return "error";
  }
  const payload = getResultPayload(toolInvocation);
  let status = "success";
  if (payload && "error" in payload && payload.error) {
    status = "error";
  }
  if (payload && "$error" in payload && payload.$error) {
    status = "error";
  }
  if (payload && "success" in payload && payload.success === "pending") {
    status = "loading";
  }
  if (payload && "success" in payload && (payload.success === false || payload.success === "false")) {
    status = "error";
  }
  if (isCancelledByUser(toolInvocation)) {
    return "stopped";
  }
  if (payload && ("cancelled" in payload && payload.cancelled === true || "status" in payload && ["cancelled", "canceled", "stopped"].includes(
    String(payload.status || "")
  ))) {
    return "stopped";
  }
  return status;
};
const getToolCallIconName = (toolName) => {
  if (/custom[_-]?action/i.test(toolName)) {
    return "navCustomAction";
  }
  if (/fill[_-]?out/i.test(toolName)) {
    return "formTextAlignLeft";
  }
  if (/validate/i.test(toolName)) {
    return "actionCompare";
  }
  if (/roll[_-]?back|checkpoint/i.test(toolName)) {
    return "miscTimelapse";
  }
  if (/delete|remove/i.test(toolName)) {
    return "actionTrash";
  }
  if (/auth/i.test(toolName)) {
    return "personSettings";
  }
  if (/mapping/i.test(toolName)) {
    return "formTextAlignLeft";
  }
  if (/test/i.test(toolName)) {
    return "actionBug";
  }
  if (/search|docs/i.test(toolName)) {
    return "navSearch";
  }
  if (/add|update|edit/i.test(toolName)) {
    return "actionEdit";
  }
  if (/rename/i.test(toolName)) {
    return "actionRename";
  }
  if (/suggest/i.test(toolName)) {
    return "miscAI";
  }
  return "actionBuild";
};
const getToolCallIconColor = (toolName) => {
  if (/delete|remove/i.test(toolName)) {
    return "StatusError";
  }
  return "UiPrimary";
};

export { getAssetType as A, getCharacterQuantity as B, consoleServerLogger as C, getUserContextAPIOrigin as D, getSemanticSearchOrigin as E, FEEDBACK_RATINGS as F, getZapierOrigin as G, HANDOFF_TOOL_NAME as H, getCustomActionsOrigin as I, HandoffStatus as J, decodeSpanIdProjectName as K, getToolCallIconName as L, getToolCallIconColor as M, camelSnakeToTitle as N, getBraintrustLogsUrl as O, SPAN_ID_HEADERS as S, makeFinalTool as a, makeServerTool as b, makeToolComponent as c, createOnToolCall as d, extractComponents as e, createErrorObject as f, createServerTools as g, commonToolParams as h, getTraceSpanId as i, isNewUserMessage as j, setSpanIdHeaders as k, createStreamResponseWithHeaders as l, makeClientTool as m, extractSpanIdForTracing as n, getToolCallStatus as o, createCopilotRegistry as p, globalCopilots as q, recurseTrim as r, serializeTools as s, getGlobalCopilotRegistry as t, trackCopilotToolExecutedAttemptEvent as u, trackCopilotUserFeedbackEvent as v, trackCopilotPredictionOutputEvent as w, trackCopilotInteractionEvent as x, getCommonTrackingEventFields as y, getUserRating as z };
//# sourceMappingURL=utils-Dwnl-Prt.mjs.map
